import Icon from '../../components/Icon/Icon';
import stableStringify from 'fast-json-stable-stringify';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import CommonButton from '../../components/Buttons/CommonButton';
import { basicsValidators } from '../../components/Campaign/validation/rules';
import { useWizardValidation } from '../../components/Campaign/validation/useWizardValidation';
import WizardAudiences from '../../components/Campaign/WizardAudiences/WizardAudiences';
import WizardBasics from '../../components/Campaign/WizardBasics/WizardBasics';
import CreativesContainer from '../../components/Campaign/WizardCreatives/CreativesContainer/CreativesContainer';
import WizardExpectedStats from '../../components/Campaign/WizardExpectedStats/WizardExpectedStats';
import WizardLaunch from '../../components/Campaign/WizardLaunch/WizardLaunch';
import WizardLocation from '../../components/Campaign/WizardLocation/WizardLocation';
import WizardReview from '../../components/Campaign/WizardReview/WizardReview';
import WizardScreens from '../../components/Campaign/WizardScreens/WizardScreens';
import WizardTab from '../../components/Campaign/WizardTab';
import Header from '../../components/Header/Header';
import Loading from '../../components/Loading/Loading';
import MapCanvas from '../../components/Maps/MapCanvas';
import Modal from '../../components/Modal/Modal';
import ModalBody from '../../components/Modal/ModalBody/ModalBody';
import ModalFooter from '../../components/Modal/ModalFooter/ModalFooter';
import ModalHeader from '../../components/Modal/ModalHeader/ModalHeader';
import { EXPECTED_STATS_UI_ENABLED } from '../../constants/constants';
import { useAuth } from '../../containers/UseAuth';
import { useForecast } from '../../contexts/ForecastContext';
import { useAppSnackbars } from '../../hooks/utils/useAppSnackbars';
import { getTierLists } from '../../services/pricingService';
import { getScreensPage } from '../../services/screensService';
import { newCampaignActions, screensActions } from '../../store';
import {
    buildGeoFilterParams,
    campaignStatus,
    getCpmMax,
    getCpmSumFromSelectedScreens,
    getWeeklyImpacts,
    numberWithoutCommas,
} from '../../utils/functions';
import { screenMatchesSearch } from '../../utils/screenSearch';
import { useService } from '../UseService';

const sanitizeRotationFilter = (value) => {
    if (value === null || value === undefined) return '';
    const normalized = String(value).trim().toLowerCase();
    return normalized.endsWith('°') ? normalized.slice(0, -1) : normalized;
};

const sanitizeSlotDurationFilter = (value) => {
    if (value === null || value === undefined) return '';
    const normalized = String(value).trim().toLowerCase();
    return normalized.endsWith('s') ? normalized.slice(0, -1) : normalized;
};

const sanitizeVenueFilter = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
};

const normalizeScreenTypeFilterValue = (value) => {
    if (value === null || value === undefined) return '';
    const trimmed = String(value).trim();
    if (!trimmed) return '';
    return trimmed.replace(/\s+/g, '_').toLowerCase();
};

const formatScreenTypeForRequest = (value) => {
    const normalized = normalizeScreenTypeFilterValue(value);
    if (!normalized) return '';
    return normalized.toUpperCase();
};

const stableSerialize = stableStringify;
const EMPTY_SCREENS = [];

const INITIAL_WIZARD_SCREENS_FILTERS = {
    q: '',
    orderBy: 'name',
    order: 'asc',
    selectedPublishers: [],
    selectedScreenTypes: [],
    selectedTiers: [],
    selectedVenueTypes: [],
    selectedRotations: [],
    selectedSlotDurations: [],
    showSelectedScreens: false,
    maxCpm: null,
    isFilterByAudiences: false,
};

const getFiltersWithoutSort = (filters = {}) => {
    const { orderBy, order, ...rest } = filters;
    return rest;
};

const getWizardScreenSortValue = (screen, orderBy) => {
    if (!screen) return '';

    if (orderBy === 'size') {
        const width = Number(screen.width);
        const height = Number(screen.height);
        if (Number.isFinite(width) && Number.isFinite(height)) {
            return width * height;
        }
        return String(screen.size ?? '');
    }

    if (orderBy === 'externalId') {
        return screen.externalId ?? screen.external_id ?? screen.id ?? '';
    }

    if (orderBy === 'publisherName') {
        return typeof screen.publisher === 'object'
            ? (screen.publisher?.name ?? '')
            : (screen.publisher ?? '');
    }

    return screen[orderBy] ?? screen.alias ?? screen.name ?? '';
};

const sortWizardScreens = (screens = [], orderBy = 'name', order = 'asc') => {
    if (!Array.isArray(screens)) return [];

    const direction = order === 'desc' ? -1 : 1;
    return [...screens].sort((a, b) => {
        if (orderBy === 'size') {
            const aWidth = Number(a?.width);
            const bWidth = Number(b?.width);
            if (Number.isFinite(aWidth) && Number.isFinite(bWidth)) {
                const widthCompare = aWidth - bWidth;
                if (widthCompare !== 0) return widthCompare * direction;
            }

            const aHeight = Number(a?.height);
            const bHeight = Number(b?.height);
            if (Number.isFinite(aHeight) && Number.isFinite(bHeight)) {
                const heightCompare = aHeight - bHeight;
                if (heightCompare !== 0) return heightCompare * direction;
            }
        }

        const aValue = getWizardScreenSortValue(a, orderBy);
        const bValue = getWizardScreenSortValue(b, orderBy);

        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return (aValue - bValue) * direction;
        }

        return (
            String(aValue).localeCompare(String(bValue), undefined, {
                numeric: true,
                sensitivity: 'base',
            }) * direction
        );
    });
};

const collectVenueFilterValues = (screen) => {
    const candidates = new Set();

    const addCandidate = (value) => {
        const sanitized = sanitizeVenueFilter(value);
        if (!sanitized) return;
        candidates.add(sanitized.toLowerCase());
    };

    const processVenue = (venue) => {
        if (!venue) return;
        if (Array.isArray(venue)) {
            venue.forEach(processVenue);
            return;
        }
        if (typeof venue === 'object') {
            addCandidate(venue.id);
            addCandidate(venue.venue_type_id);
            addCandidate(venue.value);
            addCandidate(venue.code);
            addCandidate(venue.name);
            addCandidate(venue.label);
            addCandidate(venue.description);
            addCandidate(venue.display);
        } else {
            addCandidate(venue);
        }
    };

    processVenue(screen?.venue_type);
    processVenue(screen?.venueType);
    processVenue(screen?.venue_type_id);
    processVenue(screen?.screen_type);

    return candidates;
};

// screensState
const buildComparableSnapshot = (campaign, location, basics, screensState) => ({
    newCampaign: _.omit(campaign, [
        // backend / system fields
        'status',
        'wasLaunched',
        'canLaunch',
        'validated',
        'label_id',
        'demographics',

        // UI-only
        'stepper',
        'currentWizard',
        'isResizeMapOn',
        'isResizeMapFullOn',
        'creativesDirty',
    ]),

    location: {
        selectedCountries: normalizeIds(location.selectedCountries),
        selectedRegions: normalizeIds(location.selectedRegions),
        selectedCities: normalizeIds(location.selectedCities),
        selectedNeighborhoods: normalizeIds(location.selectedNeighborhoods),
    },
    screensState: {
        selectedScreens: normalizeIds(screensState.selectedScreens),
    },
    basics,
});

// Geo selections are restored as plain ids and only become option objects once
// their level finishes loading, so both shapes must normalize to the same token
// or the change detection reports a freshly opened campaign as dirty.
const normalizeIds = (arr = []) =>
    (Array.isArray(arr) ? arr : [])
        .map((v) =>
            v && typeof v === 'object' ? (v.iso2 ?? v.id ?? v.value) : v
        )
        .filter((v) => v !== null && v !== undefined && v !== '')
        .map(String)
        .sort();

const normalizeCampaignStatusKey = (status) => {
    if (typeof status === 'string') {
        const normalizedStatus = status.trim().toUpperCase();
        const numericStatus = Number(normalizedStatus);

        if (Number.isFinite(numericStatus)) {
            return campaignStatus(numericStatus) || '';
        }

        return campaignStatus(normalizedStatus) !== undefined
            ? normalizedStatus
            : '';
    }

    return campaignStatus(status) || '';
};

const getCampaignStatusBadgeClasses = (statusKey) => {
    switch (statusKey) {
        case 'ACTIVE':
            return {
                badge: 'border-green-200 bg-green-50 text-green-700',
                dot: 'bg-green-500 animate-pulse',
            };
        case 'INACTIVE':
            return {
                badge: 'border-red-200 bg-red-50 text-red-700',
                dot: 'bg-red-500',
            };
        case 'JOB_SUCCESS':
            return {
                badge: 'border-blue-200 bg-blue-50 text-blue-700',
                dot: 'bg-blue-500',
            };
        case 'JOB_PENDING':
        case 'JOB_REJECTED':
            return {
                badge: 'border-yellow-200 bg-yellow-50 text-yellow-700',
                dot: 'bg-yellow-500',
            };
        case 'DRAFT':
        default:
            return {
                badge: 'border-gray-200 bg-gray-50 text-gray-700',
                dot: 'bg-gray-500',
            };
    }
};

const buildPoiZoneKey = (zone, index) =>
    `${index}:${String(zone?.search || zone?.label || '')}`;

const buildActivePoiFilters = (
    zones = [],
    enabled = true,
    selectedZoneKeys = null
) => {
    if (!enabled || !Array.isArray(zones)) return [];

    const filters = [];
    zones.forEach((zone, index) => {
        const isActive = zone?.active === true || zone?.active === 1;
        if (!isActive || !Array.isArray(zone?.results)) return;

        if (
            selectedZoneKeys !== null &&
            !selectedZoneKeys.has(buildPoiZoneKey(zone, index))
        )
            return;

        zone.results.forEach((result) => {
            const lat = Number(result?.lat);
            const lng = Number(result?.lng);
            const radius = Number(result?.radius);

            if (
                Number.isFinite(lat) &&
                Number.isFinite(lng) &&
                Number.isFinite(radius) &&
                radius > 0
            ) {
                filters.push({ lat, lng, radius, active: true });
            }
        });
    });

    return filters;
};

const collectVenueTypeIdsFromScreens = (screens = []) => {
    if (!Array.isArray(screens) || screens.length === 0) return [];
    const ids = new Set();

    screens.forEach((screen) => {
        collectVenueFilterValues(screen).forEach((candidate) => {
            if (/^\d+$/.test(candidate)) {
                ids.add(Number(candidate));
            }
        });
    });

    return Array.from(ids).sort((a, b) => a - b);
};

const collectVenueTypeCountsFromScreens = (screens = []) => {
    if (!Array.isArray(screens) || screens.length === 0) return [];
    const counts = new Map();

    screens.forEach((screen) => {
        collectVenueFilterValues(screen).forEach((candidate) => {
            if (!/^\d+$/.test(candidate)) return;
            const id = Number(candidate);
            counts.set(id, (counts.get(id) || 0) + 1);
        });
    });

    return Array.from(counts.entries())
        .map(([id, count]) => ({ id, count }))
        .sort((a, b) => a.id - b.id);
};

const collectRotationValuesFromScreens = (screens = []) => {
    if (!Array.isArray(screens) || screens.length === 0) return [];
    const values = new Set();

    screens.forEach((screen) => {
        const raw = screen?.rotation ?? screen?.screen_rotation;
        const normalized = sanitizeRotationFilter(raw);
        if (normalized) values.add(normalized);
    });

    return Array.from(values).sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { numeric: true })
    );
};

const collectRotationCountsFromScreens = (screens = []) => {
    if (!Array.isArray(screens) || screens.length === 0) return [];
    const counts = new Map();

    screens.forEach((screen) => {
        const raw = screen?.rotation ?? screen?.screen_rotation;
        const normalized = sanitizeRotationFilter(raw);
        if (!normalized) return;
        counts.set(normalized, (counts.get(normalized) || 0) + 1);
    });

    return Array.from(counts.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) =>
            String(a.value).localeCompare(String(b.value), undefined, {
                numeric: true,
            })
        );
};

const collectSlotDurationValuesFromScreens = (screens = []) => {
    if (!Array.isArray(screens) || screens.length === 0) return [];
    const values = new Set();

    screens.forEach((screen) => {
        const raw =
            screen?.slot_duration ??
            screen?.slotDuration ??
            screen?.slot_length ??
            screen?.slotLength;
        const normalized = sanitizeSlotDurationFilter(raw);
        if (normalized) values.add(normalized);
    });

    return Array.from(values).sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { numeric: true })
    );
};

const collectSlotDurationCountsFromScreens = (screens = []) => {
    if (!Array.isArray(screens) || screens.length === 0) return [];
    const counts = new Map();

    screens.forEach((screen) => {
        const raw =
            screen?.slot_duration ??
            screen?.slotDuration ??
            screen?.slot_length ??
            screen?.slotLength;
        const normalized = sanitizeSlotDurationFilter(raw);
        if (!normalized) return;
        counts.set(normalized, (counts.get(normalized) || 0) + 1);
    });

    return Array.from(counts.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) =>
            String(a.value).localeCompare(String(b.value), undefined, {
                numeric: true,
            })
        );
};

const collectPublisherIdsFromScreens = (screens = []) => {
    if (!Array.isArray(screens) || screens.length === 0) return [];
    const ids = new Set();

    screens.forEach((screen) => {
        const raw =
            screen?.publisher?.id ??
            screen?.publisher_id ??
            screen?.publisherId ??
            null;
        if (raw === null || raw === undefined || raw === '') return;
        const parsed = Number(raw);
        if (Number.isFinite(parsed)) ids.add(parsed);
    });

    return Array.from(ids).sort((a, b) => a - b);
};

const collectPublisherCountsFromScreens = (screens = []) => {
    if (!Array.isArray(screens) || screens.length === 0) return [];
    const counts = new Map();

    screens.forEach((screen) => {
        const raw =
            screen?.publisher?.id ??
            screen?.publisher_id ??
            screen?.publisherId ??
            null;
        if (raw === null || raw === undefined || raw === '') return;
        const id = Number(raw);
        if (!Number.isFinite(id)) return;
        counts.set(id, (counts.get(id) || 0) + 1);
    });

    return Array.from(counts.entries())
        .map(([id, count]) => ({ id, count }))
        .sort((a, b) => a.id - b.id);
};

function CreateCampaignPage() {
    const currencyDefault = JSON.parse(localStorage.getItem('currencyDefault'));
    const { t } = useTranslation();
    const { id } = useParams();
    const isCreating = !id;
    const dispatch = useDispatch();
    const history = useHistory();
    const newCampaign = useSelector((state) => state.newCampaign);
    const { screens, filteredScreens } = useSelector((state) => state.screens);
    const wallet = useSelector((state) => state.wallet);
    const budgets = wallet.budgets;
    const currencies = wallet.currencies;
    const creatives = newCampaign.creatives;
    const sizesNeeded = newCampaign.sizesNeeded;
    const campaignId = newCampaign.id;
    const effectiveCampaignPreviewId = campaignId || id || null;
    const baseName = newCampaign.baseName;
    const creativesDirty = newCampaign.creativesDirty;
    const tracking = newCampaign.tracking;
    const selectedCurrency = newCampaign.selectedCurrency;
    const budget = newCampaign.budget;
    const timing = newCampaign.timing;
    const totalSpent = Number(numberWithoutCommas(newCampaign.totalSpent)) || 0;
    const [location, setLocation] = useState({
        nodes: [],
        countries: [],
        selectedCountries: [],
        regions: [],
        selectedRegions: [],
        cities: [],
        selectedCities: [],
        neighborhood: [],
        selectedNeighborhoods: [],
        zones: [],
        validated: false,
    });

    const [basics, setBasics] = useState({
        name: '',
        brand: '',
        selectedCurrency: null,
        budget: {
            budget: 0,
            cpm: 0,
            cpmOptimizer: true,
        },
        timing: {
            isDayParting: false,
            periodValue: null, // Campaign period (start and end date)
            selectedTimezone: '',
            timezones: [],
            strategy: 'evenly', // 'evenly' | 'asap' | 'exclusivity' | 'optimizer' | 'inferred_path'
            isCellSelected: {
                cells: [],
            },
            daysEnabled: [],
            hoursEnabled: '',
            hoursOfWeek: [],
        },
        dailyCaps: {
            isActive: false,
            caps: {},
        },
        selectedCreativeCategories: [],
    });
    const mapLocations = newCampaign.mapLocations;
    const canLaunchCampaign = newCampaign.canLaunch;
    const isResizeMapOn = newCampaign.isResizeMapOn;
    const isResizeMapFullOn = newCampaign.isResizeMapFullOn;
    const zones = newCampaign.location.zones;
    const stepper = newCampaign.stepper;
    const service = useService();
    const [calculatingForecast, setCalculatingForecast] = useState(false);
    const [timer, setTimer] = useState(true);
    const hasActivePoiFilters = useMemo(
        () => buildActivePoiFilters(zones).length > 0,
        [zones]
    );
    const filterScreenByPoi =
        newCampaign.proximityFilterEnabled === true && hasActivePoiFilters;
    const setFilterScreenByPoi = useCallback(
        (nextValue) => {
            const requestedValue =
                typeof nextValue === 'function'
                    ? nextValue(filterScreenByPoi)
                    : nextValue;
            const enabled = requestedValue === true;
            const nextZones = zones.map((zone) =>
                buildActivePoiFilters([{ ...zone, active: true }]).length > 0
                    ? { ...zone, active: enabled }
                    : zone
            );
            const nextLocation = {
                ...newCampaign.location,
                zones: nextZones,
            };

            dispatch(newCampaignActions.setLocation(nextLocation));
            dispatch(newCampaignActions.setMapLocations(nextLocation));
            dispatch(newCampaignActions.setProximityFilterEnabled(enabled));
        },
        [dispatch, filterScreenByPoi, newCampaign.location, zones]
    );
    // null = all zones selected, Set<string> = specific zone keys selected
    const [selectedPoiZoneKeys, setSelectedPoiZoneKeys] = useState(null);
    const [screenOpened, setScreenOpened] = useState(null);
    const [isLoadedData, setIsLoadedData] = useState(false);
    const [fetchingData, setFetchingData] = useState(false);
    const [loadingDataTable, setLoadingDataTable] = useState(false);
    const [showModalWarning, setShowModalWarning] = useState(false);
    const [shouldDeactivateOnSave, setShouldDeactivateOnSave] = useState(false);
    const [resetMap, setResetMap] = useState(true);
    const resizeMapTransitionDuration = 300;
    const [campaignNameWarning, setCampaignNameWarning] = useState('');
    const [isFirstTime, setIsFirstTime] = useState(true);
    const [screensState, setScreensState] = useState({
        selectedScreens: [],
    });
    const [savedSelectedScreenIds, setSavedSelectedScreenIds] = useState([]);
    const expectedStats = newCampaign.expectedStats || {};
    const idCampaignRedux = newCampaign.id;
    const isSame = idCampaignRedux === parseInt(id);
    const mapedScreens = EMPTY_SCREENS;
    const { selectedScreensLocal, tierList, tiers } = useSelector(
        (state) => state.screens
    );
    const [localScreensFilters, setWizardScreensFilters] = useState(
        INITIAL_WIZARD_SCREENS_FILTERS
    );
    const wizardScreensFilters = useMemo(
        () => ({
            ...localScreensFilters,
            providerSource: newCampaign.providerSource,
        }),
        [localScreensFilters, newCampaign.providerSource]
    );
    const [wizardScreensPage, setWizardScreensPage] = useState(1);
    const [wizardScreensPerPage, setWizardScreensPerPage] = useState(100);
    const [wizardScreensData, setWizardScreensData] = useState({
        results: [],
        total: 0,
        availableVenueTypeIds: [],
        availableVenueTypeCounts: [],
        availableRotations: [],
        availableRotationCounts: [],
        availableSlotDurations: [],
        availableSlotDurationCounts: [],
        availablePublisherIds: [],
        availablePublisherCounts: [],
    });
    const {
        results: filteredScreensByLocation,
        total: wizardScreensTotalRows,
        availableVenueTypeIds: wizardAvailableVenueTypeIds,
        availableVenueTypeCounts: wizardAvailableVenueTypeCounts,
        availableRotations: wizardAvailableRotations,
        availableRotationCounts: wizardAvailableRotationCounts,
        availableSlotDurations: wizardAvailableSlotDurations,
        availableSlotDurationCounts: wizardAvailableSlotDurationCounts,
        availablePublisherIds: wizardAvailablePublisherIds,
        availablePublisherCounts: wizardAvailablePublisherCounts,
    } = wizardScreensData;
    const setFilteredScreensByLocation = useCallback((nextResults) => {
        setWizardScreensData((previous) => ({
            ...previous,
            results:
                typeof nextResults === 'function'
                    ? nextResults(previous.results)
                    : nextResults,
        }));
    }, []);
    const wizardFiltersStringRef = useRef(
        stableSerialize(INITIAL_WIZARD_SCREENS_FILTERS)
    );
    const [initialCampaignState, setInitialCampaignState] = useState(null);
    const [isSaveEnabled, setIsSaveEnabled] = useState(false);
    const [isCreativesDirty, setIsCreativesDirty] = useState(false);
    const [prevState, setPrevState] = useState(''); // Previous state for comparison

    const [isSaving, setIsSaving] = useState(false);
    const confirmedDeactivateOnSaveRef = useRef(false);
    const { setForecast, setForecastTable, resetForecast } = useForecast();

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (isSaveEnabled) {
                event.preventDefault();
                event.returnValue = ''; // Required for Chrome/Firefox
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        // Cleanup the listener when the component unmounts
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isSaveEnabled]); // Re-run effect if isDirty changes

    const updateBasics = useCallback(
        (key, value) => {
            if (key === 'selectedCurrency') {
                const currentCurrencyId = basics?.selectedCurrency?.id ?? null;
                const nextCurrencyId = value?.id ?? null;
                const currencyChanged = currentCurrencyId !== nextCurrencyId;

                if (currencyChanged) {
                    dispatch(screensActions.setSelectedScreensLocal([]));
                    dispatch(newCampaignActions.setSelectedScreens([]));
                    setScreensState((prev) => ({
                        ...prev,
                        selectedScreens: [],
                    }));
                    setScreenOpened(null);
                }
            }

            setBasics((prev) => ({
                ...prev,
                [key]: value,
            }));
        },
        [basics?.selectedCurrency?.id, dispatch]
    );

    // Mirror the Basics currency into the store: screen details and markers
    // read it from there (TGF-1240).
    useEffect(() => {
        const basicsCurrency = basics?.selectedCurrency;
        if (!basicsCurrency?.id) return;
        if (basicsCurrency.id === selectedCurrency?.id) return;
        dispatch(newCampaignActions.setSelectedCurrency(basicsCurrency));
    }, [basics?.selectedCurrency, selectedCurrency?.id, dispatch]);

    const { openSuccess, openError } = useAppSnackbars();

    const [creativesState, setCreativesState] = useState();
    const [hasMountedCreatives, setHasMountedCreatives] = useState(
        stepper.wizard === 'creatives'
    );

    useEffect(() => {
        if (stepper.wizard === 'creatives') {
            setHasMountedCreatives(true);
        }
    }, [stepper.wizard]);

    const currentSelectedScreenIds = useMemo(
        () => normalizeIds(screensState.selectedScreens),
        [screensState.selectedScreens]
    );
    const validator = useWizardValidation(
        basicsValidators(
            { ...basics, location, ...screensState },
            budgets,
            totalSpent,
            newCampaign.timing?.periodValue?.[0]
        )
    );
    const hasUnsavedSelectedScreens =
        JSON.stringify(currentSelectedScreenIds) !==
        JSON.stringify(savedSelectedScreenIds);
    const campaignStatusKey =
        normalizeCampaignStatusKey(newCampaign.status) ||
        (isCreating ? 'DRAFT' : '');
    const isLaunchBlockedByActiveCampaign = campaignStatusKey === 'ACTIVE';
    const campaignStatusBadgeClasses =
        getCampaignStatusBadgeClasses(campaignStatusKey);

    const onSave = useCallback(async () => {
        validator.validateAll();
        if (!validator.isValid()) {
            setIsSaving(false);
            return false;
        }

        if (shouldDeactivateOnSave && !confirmedDeactivateOnSaveRef.current) {
            setShowModalWarning(true);
            return false;
        }

        setIsSaving(true);
        let creativeLinksPersisted = !creativesDirty;
        const shouldPersistCreativeLinks =
            Boolean(creativesRef.current) &&
            (stepper.wizard === 'creatives' || creativesDirty);
        const locationForSave = {
            ...location,
            zones: Array.isArray(newCampaign?.location?.zones)
                ? newCampaign.location.zones
                : Array.isArray(location?.zones)
                  ? location.zones
                  : [],
        };

        if (shouldPersistCreativeLinks) {
            const canPersistCreativeLinks =
                creativesRef.current?.validateLinksReadyForSave?.() ?? true;

            if (!canPersistCreativeLinks) {
                setIsSaving(false);
                return false;
            }
        }

        if (shouldDeactivateOnSave) {
            const deactivated = await service.deactivateCampaign({
                ...newCampaign,
                id: campaignId || id,
                status: 'ACTIVE',
                isActive: true,
            });

            if (!deactivated) {
                setIsSaveEnabled(true);
                setIsSaving(false);
                return false;
            }

            confirmedDeactivateOnSaveRef.current = false;
            setShouldDeactivateOnSave(false);
            dispatch(newCampaignActions.setStatus(campaignStatus('INACTIVE')));
        }

        if (!id) {
            try {
                const newId = await service.saveOrUpdateCampaignPreview(
                    basics,
                    locationForSave,
                    screensState
                );

                let currentState = buildComparableSnapshot(
                    newCampaign,
                    locationForSave,
                    basics,
                    screensState
                );

                currentState.newCampaign.id = newId; // Ensure the new ID is included in the snapshot.
                setPrevState(currentState);
                setInitialCampaignState(currentState);
                setSavedSelectedScreenIds(currentSelectedScreenIds);

                history.push(`/campaign/edit/${newId}`);
            } catch (error) {
                openError(
                    t('sections.campaigns.new-campaign.save-campaign-error')
                );
                setIsSaving(false);
                return false;
            }
        } else {
            try {
                await service.saveOrUpdateCampaignPreview(
                    basics,
                    locationForSave,
                    screensState
                );

                let currentState = buildComparableSnapshot(
                    newCampaign,
                    locationForSave,
                    basics,
                    screensState
                );

                setInitialCampaignState(currentState);
                setPrevState(currentState);
                setSavedSelectedScreenIds(currentSelectedScreenIds);
            } catch (error) {
                openError(
                    t('sections.campaigns.new-campaign.save-campaign-error')
                );
                setIsSaving(false);
                return false;
            }
        }

        if (shouldPersistCreativeLinks) {
            const request = creativesRef.current?.buildLinkRequest?.() ?? [];

            if (request.length) {
                const linked =
                    await service.linkMultipleCreativesToScreens(request);

                if (!linked) {
                    setIsCreativesDirty(true);
                    dispatch(newCampaignActions.setCreativesDirty(true));
                    setIsSaveEnabled(true);
                    setIsSaving(false);
                    return false;
                }
            }

            creativesRef.current?.markLinksPersisted?.();
            creativeLinksPersisted = true;
        }

        setIsCreativesDirty(!creativeLinksPersisted);
        dispatch(newCampaignActions.setCreativesDirty(!creativeLinksPersisted));
        setIsSaveEnabled(!creativeLinksPersisted);
        setIsSaving(false);
        openSuccess(t('sections.campaigns.new-campaign.save-campaign-succes'));
        return true;
    }, [
        basics,
        campaignId,
        currentSelectedScreenIds,
        id,
        location,
        newCampaign,
        screensState,
        service,
        creativesDirty,
        history,
        openSuccess,
        openError,
        shouldDeactivateOnSave,
        stepper.wizard,
        t,
        validator,
        totalSpent,
    ]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();

                if (isSaveEnabled) {
                    onSave();
                }
            }
        };

        window.addEventListener('keydown', onKeyDown);

        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [onSave, isSaveEnabled]);

    useEffect(() => {
        if (isLoadedData) {
            setInitialCampaignState({
                newCampaign,
                location,
                basics,
                screensState,
            });
        }
    }, [isLoadedData, selectedCurrency, location, basics]);

    useEffect(() => {
        setIsCreativesDirty(creativesDirty);
    }, [creativesDirty]);

    const hasInitializedRef = useRef(false);

    useEffect(() => {
        if (!initialCampaignState) return;

        if (isCreativesDirty) {
            setIsSaveEnabled(true);
            return;
        }

        let newState = buildComparableSnapshot(
            newCampaign,
            location,
            basics,
            screensState
        );

        // Initialize state
        // HACK: the location state is set after loading, I must ensure that the
        // prevState is set with the location data to avoid false positives in
        // change detection
        if (
            prevState === '' &&
            initialCampaignState.location?.countries?.length > 0
        ) {
            setPrevState(newState);
        }

        let hasChanges = JSON.stringify(newState) !== JSON.stringify(prevState);

        // HACK: I hate myself for this, but the age segment has a weird behavior where
        // it updates the sliderValue with the same value, causing the state to
        // change and the save button to be enabled even when there are no real
        // changes. This is a quick fix to prevent that from happening, but it
        // should be refactored in the future to avoid this kind of issues.
        if (
            newState.newCampaign?.segments?.age?.sliderValue[0] + 1 ===
            prevState.newCampaign?.segments?.age?.sliderValue[0]
        ) {
            let tmp = JSON.parse(JSON.stringify(prevState));
            tmp.newCampaign.segments.age.sliderValue[0] =
                newState.newCampaign.segments.age.sliderValue[0];
            setPrevState(tmp);
            hasChanges = JSON.stringify(newState) !== JSON.stringify(tmp);
        }

        setIsSaveEnabled(hasChanges);
    }, [initialCampaignState, isCreativesDirty, screensState, newCampaign]);

    const setupInitialCampaignState = useCallback(() => {
        if (!isLoadedData) return;
        if (hasInitializedRef.current) return;
        hasInitializedRef.current = true;

        setInitialCampaignState(
            buildComparableSnapshot(newCampaign, location, basics, screensState)
        );

        setIsCreativesDirty(false);
        dispatch(newCampaignActions.setCreativesDirty(false));
        setSavedSelectedScreenIds(normalizeIds(screensState.selectedScreens));
        setIsSaveEnabled(false);
    }, [basics, dispatch, isLoadedData, location, newCampaign, screensState]);

    useEffect(() => {
        setupInitialCampaignState();
    }, [setupInitialCampaignState]);

    //it brings the tiers and make a key value object with id-screenType
    const fetchTierListAsIdName = async () => {
        if (!Object.keys(tierList).length) {
            let tiersFromApi = await getTierLists();
            let tiersByNames = {};

            let tiersByIds = {};
            tiersFromApi.message.forEach((tier) => {
                tiersByNames[tier.screenType] = tiersByNames[tier.screenType]
                    ? [...tiersByNames[tier.screenType], tier.id]
                    : [tier.id];
                tiersByIds[tier.id] = tier.screenType;
            });
            dispatch(
                screensActions.setTiers(
                    Object.keys(tiersByNames).map((keyName) => ({
                        screenType: keyName,
                        ids: tiersByNames[keyName],
                    }))
                )
            );
            dispatch(screensActions.setTierList(tiersByIds));
            return;
        } else {
            return;
        }
    };
    useEffect(() => {
        fetchTierListAsIdName();
    }, []);

    useEffect(() => {
        if (!id) {
            dispatch(screensActions.setSelectedScreensLocal([]));
            dispatch(newCampaignActions.setSelectedScreens([]));
        }
    }, [id, dispatch]);

    const setSelectedScreensLocal = useCallback(
        (data) => dispatch(screensActions.setSelectedScreensLocal(data)),
        [dispatch]
    );
    const handleWizardScreensFiltersChange = useCallback(
        (nextFilters) => {
            const nextSerialized = stableSerialize(nextFilters);
            if (nextSerialized === wizardFiltersStringRef.current) return;

            const currentFiltersWithoutSort = stableSerialize(
                getFiltersWithoutSort(wizardScreensFilters)
            );
            const nextFiltersWithoutSort = stableSerialize(
                getFiltersWithoutSort(nextFilters)
            );
            const onlySortChanged =
                currentFiltersWithoutSort === nextFiltersWithoutSort;

            wizardFiltersStringRef.current = nextSerialized;
            const { providerSource, ...localFilters } = nextFilters;
            setWizardScreensFilters(localFilters);
            if (
                stableSerialize(providerSource) !==
                stableSerialize(newCampaign.providerSource)
            ) {
                dispatch(newCampaignActions.setProviderSource(providerSource));
            }
            if (!onlySortChanged) {
                setWizardScreensPage(1);
            }
        },
        [wizardScreensFilters, newCampaign.providerSource, dispatch]
    );

    useEffect(() => {
        wizardFiltersStringRef.current = stableSerialize(wizardScreensFilters);
    }, [wizardScreensFilters]);

    const handleWizardScreensPageChange = useCallback((nextPage) => {
        setWizardScreensPage(nextPage);
    }, []);

    const handleWizardScreensPerPageChange = useCallback((nextPerPage) => {
        setWizardScreensPerPage(nextPerPage);
        setWizardScreensPage(1);
    }, []);

    const handlePoiZoneKeysChange = useCallback(
        (newKeys) => {
            const zones = Array.isArray(newCampaign?.location?.zones)
                ? newCampaign.location.zones
                : [];

            if (zones.length) {
                const newZones = zones.map((zone, index) => {
                    // Only manage zones that have valid location data (those shown in FilterByPoi)
                    const hasValidResults =
                        Array.isArray(zone?.results) &&
                        zone.results.some((r) => {
                            const lat = Number(r?.lat);
                            const lng = Number(r?.lng);
                            const radius = Number(r?.radius);
                            return (
                                Number.isFinite(lat) &&
                                Number.isFinite(lng) &&
                                Number.isFinite(radius) &&
                                radius > 0
                            );
                        });
                    if (!hasValidResults) return zone;

                    const key = buildPoiZoneKey(zone, index);
                    const wasActive =
                        zone?.active === true || zone?.active === 1;
                    const shouldBeActive = newKeys === null || newKeys.has(key);
                    return shouldBeActive === wasActive
                        ? zone
                        : { ...zone, active: shouldBeActive };
                });

                if (newZones.some((z, i) => z !== zones[i])) {
                    const newLocation = {
                        ...newCampaign.location,
                        zones: newZones,
                    };
                    dispatch(newCampaignActions.setLocation(newLocation));
                    dispatch(newCampaignActions.setMapLocations(newLocation));
                }
            }

            // zone.active is now the source of truth; null means "all active zones selected"
            setSelectedPoiZoneKeys(null);
        },
        [newCampaign, dispatch]
    );

    const handleWizardScreensSelectionChange = useCallback(
        (selectedRows) => {
            setSelectedScreensLocal(selectedRows);
            setScreensState({ selectedScreens: selectedRows });
        },
        [dispatch, setSelectedScreensLocal]
    );

    const hasDiferrentScreens =
        selectedScreensLocal.length === 0 &&
        screensState.selectedScreens.length > 0;
    //seting selectedLocalscreens when opening campaign
    useEffect(() => {
        if (!id) return;
        if (isFirstTime && hasDiferrentScreens) {
            if (id && !isSame) return;
            else {
                setSelectedScreensLocal(screensState.selectedScreens);
                setIsFirstTime(false);
            }
        }
    }, [screensState.selectedScreens]);

    const [filteredScreensLocal, setFilteredScreensLocal] = useState([]);
    useEffect(() => {
        if (screenOpened) {
            let isInList = filteredScreensLocal.find(
                (screen) => screen.id == screenOpened.id
            );
            if (!isInList) {
                setScreenOpened(null);
            }
        }
    }, [filteredScreensLocal]);

    useEffect(() => {
        if (stepper.wizard !== 'screens') return;
        if (!filteredScreensLocal.length) return;
        setFilteredScreensLocal([]);
    }, [stepper.wizard, filteredScreensLocal.length]);

    const screensWithCpm = EMPTY_SCREENS;
    const auth = useAuth();
    const userId = auth.session.user.id;
    const userMargin = auth?.session?.user?.rtb_margin || 0;
    const canFilterByPublisher = auth.isAdmin() || auth.isSupervising();
    const canManageExpectedStats =
        canFilterByPublisher && EXPECTED_STATS_UI_ENABLED;
    const selectedCurrencyName =
        basics?.selectedCurrency?.short_name ||
        selectedCurrency?.short_name ||
        currencyDefault?.short_name;

    const usingLocalScreens = filteredScreensLocal.length > 0;

    const screensForFilters = usingLocalScreens
        ? filteredScreensLocal
        : filteredScreensByLocation;

    const providerFilters = useMemo(() => {
        const rawValues = Array.isArray(wizardScreensFilters.providerSource)
            ? wizardScreensFilters.providerSource
            : wizardScreensFilters.providerSource
              ? [wizardScreensFilters.providerSource]
              : [];
        return rawValues
            .map((value) => String(value ?? '').trim())
            .filter(Boolean);
    }, [wizardScreensFilters.providerSource]);

    const screenPassesFilters = useMemo(() => {
        const {
            selectedPublishers,
            selectedScreenTypes,
            selectedTiers,
            selectedVenueTypes,
            selectedRotations,
            selectedSlotDurations,
            maxCpm,
            q,
            isFilterByAudiences,
        } = wizardScreensFilters;

        const providerAliasMap = {
            tgx: ['tgx', 'taggify'],
            viooh: ['viooh'],
            outcon: ['outcon'],
        };

        const providerSet = providerFilters.length
            ? new Set(
                  providerFilters.flatMap((p) => {
                      const key = String(p || '').toLowerCase();
                      return providerAliasMap[key] || [key];
                  })
              )
            : null;

        const sanitizedPublishers =
            canFilterByPublisher && Array.isArray(selectedPublishers)
                ? selectedPublishers.filter((publisher, index, arr) => {
                      if (!publisher || publisher.id == null) return false;
                      const firstIndex = arr.findIndex(
                          (item) => item?.id === publisher.id
                      );
                      return firstIndex === index;
                  })
                : [];

        const publisherIds = new Set(
            sanitizedPublishers
                .map((publisher) => publisher?.id)
                .filter((id) => id != null)
        );
        const publisherNames = new Set(
            sanitizedPublishers
                .map((publisher) => (publisher?.name || '').toLowerCase())
                .filter(Boolean)
        );

        const screenTypeFilters = new Set(
            (selectedScreenTypes || [])
                .map((value) => {
                    if (typeof value === 'string') {
                        return normalizeScreenTypeFilterValue(value);
                    }
                    if (value && typeof value === 'object') {
                        return normalizeScreenTypeFilterValue(
                            value.value ?? value.name ?? value.type ?? value.id
                        );
                    }
                    return '';
                })
                .filter(Boolean)
        );

        const venueTypeFilters = new Set(
            (selectedVenueTypes || [])
                .map((value) => sanitizeVenueFilter(value).toLowerCase())
                .filter(Boolean)
        );

        const rotationValues = new Set(
            (selectedRotations || [])
                .map((value) => sanitizeRotationFilter(value))
                .filter(Boolean)
        );

        const slotDurationValues = new Set(
            (selectedSlotDurations || [])
                .map((value) => sanitizeSlotDurationFilter(value))
                .filter(Boolean)
        );

        const tierIds = new Set(
            (selectedTiers || [])
                .flatMap((tier) => (Array.isArray(tier?.ids) ? tier.ids : []))
                .filter(Boolean)
        );

        const normalizedSearch = q ? q.trim().toLowerCase() : '';
        const margin = typeof userMargin === 'number' ? userMargin : 0;
        const denominator = 1 - margin === 0 ? 1 : 1 - margin;
        const hasMaxCpm = typeof maxCpm === 'number' && !Number.isNaN(maxCpm);
        const isAdmin = auth.isAdmin();

        return (screen) => {
            if (!screen) return false;

            if (providerSet) {
                const providerName =
                    typeof screen.provider === 'string'
                        ? screen.provider
                        : screen.provider?.name || '';
                if (
                    providerName &&
                    !providerSet.has(providerName.toLowerCase())
                )
                    return false;
            }

            if (
                canFilterByPublisher &&
                (publisherIds.size || publisherNames.size)
            ) {
                const publisherId =
                    typeof screen.publisher === 'object'
                        ? screen.publisher?.id
                        : undefined;
                const publisherName = (
                    typeof screen.publisher === 'string'
                        ? screen.publisher
                        : screen.publisher?.name || ''
                ).toLowerCase();

                let matchesPublisher = false;
                if (
                    publisherId &&
                    publisherIds.size &&
                    publisherIds.has(publisherId)
                ) {
                    matchesPublisher = true;
                } else if (
                    publisherNames.size &&
                    publisherNames.has(publisherName)
                ) {
                    matchesPublisher = true;
                }

                if (!matchesPublisher) return false;
            }

            if (screenTypeFilters.size) {
                // API response uses screen_location for the INDOOR/OUTDOOR enum;
                // screen.screenType is the camelCase alias used in some contexts.
                const rawType =
                    screen.screenType ??
                    screen.screen_location ??
                    screen.screen_type;
                const typeName = normalizeScreenTypeFilterValue(rawType);
                if (!screenTypeFilters.has(typeName)) return false;
            }

            if (venueTypeFilters.size) {
                const venueCandidates = collectVenueFilterValues(screen);
                if (
                    !Array.from(venueCandidates).some((candidate) =>
                        venueTypeFilters.has(candidate)
                    )
                ) {
                    return false;
                }
            }

            if (rotationValues.size) {
                const rotation = screen?.rotation ?? screen?.screen_rotation;
                const normalizedRotation =
                    rotation === null || rotation === undefined
                        ? ''
                        : String(rotation).toLowerCase();
                if (!rotationValues.has(normalizedRotation)) return false;
            }

            if (slotDurationValues.size) {
                const slotDuration =
                    screen?.slot_duration ??
                    screen?.slotDuration ??
                    screen?.slot_length ??
                    screen?.slotLength;
                const normalizedSlotDuration =
                    sanitizeSlotDurationFilter(slotDuration);
                if (
                    !normalizedSlotDuration ||
                    !slotDurationValues.has(normalizedSlotDuration)
                )
                    return false;
            }

            if (tierIds.size) {
                if (!screen.tierId || !tierIds.has(screen.tierId)) return false;
            }

            const hasAudienceData =
                typeof screen?.hasAudience === 'boolean'
                    ? screen.hasAudience
                    : getWeeklyImpacts(screen) > 0;
            if (isFilterByAudiences && !hasAudienceData) return false;

            if (selectedCurrencyName) {
                const hasCurrencyInLegacyDeals = screen.deals?.prices?.some(
                    (item) =>
                        String(item?.currency || '').toUpperCase() ===
                        String(selectedCurrencyName).toUpperCase()
                );
                const hasCurrencyInNestedDeals = Boolean(
                    screen?.deals?.[selectedCurrencyName]
                );
                const hasCurrency =
                    hasCurrencyInLegacyDeals || hasCurrencyInNestedDeals;
                if (!hasCurrency) return false;
            }

            const hasValidLegacyPrice = screen.deals?.prices?.some(
                (item) => Number(item?.price) > 0
            );
            const currencyDeals = selectedCurrencyName
                ? screen?.deals?.[selectedCurrencyName]
                : null;
            const hasValidNestedPrice =
                Number(currencyDeals?.CPM?.max_cpm) > 0 ||
                Number(currencyDeals?.SPOT?.rules?.[0]?.cpm) > 0 ||
                Number(currencyDeals?.SPM?.rules?.[0]?.cpm) > 0;
            const hasValidPrice = hasValidLegacyPrice || hasValidNestedPrice;
            if (!hasValidPrice) return false;

            if (!isAdmin) {
                const label = String(
                    screen.label || screen.name || ''
                ).toLowerCase();
                if (label.includes('test')) return false;
            }

            if (hasMaxCpm) {
                const cpmFromDeals = Number(
                    screen?.deals?.[selectedCurrencyName]?.CPM?.max_cpm
                );
                const rawEcpm =
                    typeof screen.ecpm === 'number' &&
                    !Number.isNaN(screen.ecpm)
                        ? screen.ecpm
                        : Number.isFinite(cpmFromDeals) && cpmFromDeals > 0
                          ? cpmFromDeals
                          : getCpmMax([screen]);
                if (!rawEcpm || Number.isNaN(rawEcpm)) return false;
                const adjusted = rawEcpm / (denominator || 1);
                if (adjusted > maxCpm) return false;
            }

            if (
                normalizedSearch &&
                !screenMatchesSearch(screen, normalizedSearch)
            ) {
                return false;
            }

            return true;
        };
    }, [
        wizardScreensFilters,
        auth,
        selectedCurrencyName,
        userMargin,
        providerFilters,
        canFilterByPublisher,
    ]);

    const screenPassesSelectionFilters = useMemo(() => {
        const {
            selectedPublishers,
            selectedScreenTypes,
            selectedVenueTypes,
            selectedRotations,
            selectedSlotDurations,
            q,
            isFilterByAudiences,
        } = wizardScreensFilters;

        const sanitizedPublishers =
            canFilterByPublisher && Array.isArray(selectedPublishers)
                ? selectedPublishers.filter((publisher, index, arr) => {
                      if (!publisher || publisher.id == null) return false;
                      const firstIndex = arr.findIndex(
                          (item) => item?.id === publisher.id
                      );
                      return firstIndex === index;
                  })
                : [];

        const publisherIds = new Set(
            sanitizedPublishers.map((p) => p?.id).filter((id) => id != null)
        );
        const publisherNames = new Set(
            sanitizedPublishers
                .map((p) => (p?.name || '').toLowerCase())
                .filter(Boolean)
        );

        const screenTypeFilters = new Set(
            (selectedScreenTypes || [])
                .map((value) => {
                    if (typeof value === 'string')
                        return normalizeScreenTypeFilterValue(value);
                    if (value && typeof value === 'object')
                        return normalizeScreenTypeFilterValue(
                            value.value ?? value.name ?? value.type ?? value.id
                        );
                    return '';
                })
                .filter(Boolean)
        );

        const venueTypeFilters = new Set(
            (selectedVenueTypes || [])
                .map((value) => sanitizeVenueFilter(value).toLowerCase())
                .filter(Boolean)
        );

        const rotationValues = new Set(
            (selectedRotations || [])
                .map((value) => sanitizeRotationFilter(value))
                .filter(Boolean)
        );

        const slotDurationValues = new Set(
            (selectedSlotDurations || [])
                .map((value) => sanitizeSlotDurationFilter(value))
                .filter(Boolean)
        );

        const normalizedSearch = q ? q.trim().toLowerCase() : '';

        return (screen) => {
            if (!screen) return false;

            if (
                canFilterByPublisher &&
                (publisherIds.size || publisherNames.size)
            ) {
                const publisherId =
                    typeof screen.publisher === 'object'
                        ? screen.publisher?.id
                        : undefined;
                const publisherName = (
                    typeof screen.publisher === 'string'
                        ? screen.publisher
                        : screen.publisher?.name || ''
                ).toLowerCase();
                const matchesPublisher =
                    (publisherId &&
                        publisherIds.size &&
                        publisherIds.has(publisherId)) ||
                    (publisherNames.size && publisherNames.has(publisherName));
                if (!matchesPublisher) return false;
            }

            if (screenTypeFilters.size) {
                const rawType =
                    screen.screenType ??
                    screen.screen_location ??
                    screen.screen_type;
                const typeName = normalizeScreenTypeFilterValue(rawType);
                if (!screenTypeFilters.has(typeName)) return false;
            }

            if (venueTypeFilters.size) {
                const venueCandidates = collectVenueFilterValues(screen);
                if (
                    !Array.from(venueCandidates).some((c) =>
                        venueTypeFilters.has(c)
                    )
                )
                    return false;
            }

            if (rotationValues.size) {
                const rotation = screen?.rotation ?? screen?.screen_rotation;
                const normalizedRotation =
                    rotation === null || rotation === undefined
                        ? ''
                        : String(rotation).toLowerCase();
                if (!rotationValues.has(normalizedRotation)) return false;
            }

            if (slotDurationValues.size) {
                const slotDuration =
                    screen?.slot_duration ??
                    screen?.slotDuration ??
                    screen?.slot_length ??
                    screen?.slotLength;
                const normalizedSlotDuration =
                    sanitizeSlotDurationFilter(slotDuration);
                if (
                    !normalizedSlotDuration ||
                    !slotDurationValues.has(normalizedSlotDuration)
                )
                    return false;
            }

            const hasAudienceData =
                typeof screen?.hasAudience === 'boolean'
                    ? screen.hasAudience
                    : getWeeklyImpacts(screen) > 0;
            if (isFilterByAudiences && !hasAudienceData) return false;

            if (
                normalizedSearch &&
                !screenMatchesSearch(screen, normalizedSearch)
            ) {
                return false;
            }

            return true;
        };
    }, [wizardScreensFilters, canFilterByPublisher]);

    const filteredScreensForTable = useMemo(() => {
        const baseScreens = Array.isArray(screensForFilters)
            ? screensForFilters
            : [];
        if (!baseScreens.length) return [];
        const sortedScreens = usingLocalScreens
            ? sortWizardScreens(
                  baseScreens,
                  wizardScreensFilters.orderBy,
                  wizardScreensFilters.order
              )
            : baseScreens;
        if (stepper.wizard !== 'screens') {
            return sortedScreens.slice(
                0,
                usingLocalScreens ? wizardScreensPerPage : baseScreens.length
            );
        }
        return sortedScreens.filter(screenPassesFilters);
    }, [
        screensForFilters,
        screenPassesFilters,
        stepper.wizard,
        usingLocalScreens,
        wizardScreensPerPage,
        wizardScreensFilters.orderBy,
        wizardScreensFilters.order,
    ]);

    const paginatedScreens = useMemo(() => {
        if (!usingLocalScreens) return filteredScreensForTable;

        const start = (wizardScreensPage - 1) * wizardScreensPerPage;
        return filteredScreensForTable.slice(
            start,
            start + wizardScreensPerPage
        );
    }, [
        filteredScreensForTable,
        usingLocalScreens,
        wizardScreensPage,
        wizardScreensPerPage,
    ]);

    const totalFilteredScreens = filteredScreensForTable.length;
    const hasClientOnlyFilters =
        (wizardScreensFilters.selectedTiers?.length ?? 0) > 0 ||
        Boolean(wizardScreensFilters.showSelectedScreens) ||
        Boolean(wizardScreensFilters.isFilterByAudiences);

    const hasNonNumericVenueFilters = (
        wizardScreensFilters.selectedVenueTypes || []
    ).some((value) => {
        const sanitized = sanitizeVenueFilter(value);
        return sanitized && !/^\d+$/.test(sanitized);
    });

    const shouldUseLocalTotals =
        usingLocalScreens || hasClientOnlyFilters || hasNonNumericVenueFilters;

    const totalRowsForPagination = shouldUseLocalTotals
        ? totalFilteredScreens
        : wizardScreensTotalRows;

    useEffect(() => {
        if (stepper.wizard !== 'screens') return;

        if (isCreating && !usingLocalScreens) {
            if (!selectedCurrencyName) return;

            let cancelled = false;

            const loadScreens = async () => {
                setLoadingDataTable(true);
                try {
                    const searchParam = wizardScreensFilters.q || undefined;
                    const providerParam = providerFilters.length
                        ? providerFilters.join(',')
                        : undefined;
                    const rotationValuesParam = Array.isArray(
                        wizardScreensFilters.selectedRotations
                    )
                        ? wizardScreensFilters.selectedRotations
                              .map((value) => sanitizeRotationFilter(value))
                              .filter(Boolean)
                        : [];
                    const slotDurationValuesParam = Array.isArray(
                        wizardScreensFilters.selectedSlotDurations
                    )
                        ? wizardScreensFilters.selectedSlotDurations
                              .map((value) => sanitizeSlotDurationFilter(value))
                              .filter(Boolean)
                        : [];
                    const venueTypeValuesParam = Array.isArray(
                        wizardScreensFilters.selectedVenueTypes
                    )
                        ? wizardScreensFilters.selectedVenueTypes
                              .map((value) => sanitizeVenueFilter(value))
                              .filter(Boolean)
                        : [];
                    const venueTypeIdValues = venueTypeValuesParam.filter(
                        (value) => /^\d+$/.test(value)
                    );
                    const screenTypeValuesParam = Array.isArray(
                        wizardScreensFilters.selectedScreenTypes
                    )
                        ? wizardScreensFilters.selectedScreenTypes
                              .map((value) => {
                                  if (typeof value === 'string') {
                                      return formatScreenTypeForRequest(value);
                                  }
                                  if (value && typeof value === 'object') {
                                      return formatScreenTypeForRequest(
                                          value.value ??
                                              value.name ??
                                              value.type ??
                                              value.id
                                      );
                                  }
                                  return '';
                              })
                              .filter(Boolean)
                        : [];
                    const publisherIdValues =
                        canFilterByPublisher &&
                        Array.isArray(wizardScreensFilters.selectedPublishers)
                            ? wizardScreensFilters.selectedPublishers
                                  .map((publisher) => publisher?.id)
                                  .filter((id) => id != null)
                            : [];

                    const rotationParam = rotationValuesParam.length
                        ? Array.from(new Set(rotationValuesParam)).join(',')
                        : undefined;
                    const slotDurationParam = slotDurationValuesParam.length
                        ? Array.from(new Set(slotDurationValuesParam)).join(',')
                        : undefined;
                    const venueTypeIdParam = venueTypeIdValues.length
                        ? Array.from(new Set(venueTypeIdValues)).join(',')
                        : undefined;
                    const screenTypeParam = screenTypeValuesParam.length
                        ? Array.from(new Set(screenTypeValuesParam)).join(',')
                        : undefined;
                    const publisherIdParam = publisherIdValues.length
                        ? Array.from(new Set(publisherIdValues)).join(',')
                        : undefined;

                    const geoParams = buildGeoFilterParams(location);
                    const poiFilters = buildActivePoiFilters(
                        newCampaign?.location?.zones,
                        filterScreenByPoi,
                        selectedPoiZoneKeys
                    );
                    const {
                        results,
                        total,
                        availableVenueTypeIds,
                        availableRotations,
                        availableSlotDurations,
                        availablePublisherIds,
                        availableVenueTypeCounts,
                        availableRotationCounts,
                        availableSlotDurationCounts,
                        availablePublisherCounts,
                    } = await getScreensPage({
                        page: wizardScreensPage,
                        limit: wizardScreensPerPage,
                        orderBy: wizardScreensFilters.orderBy,
                        order: wizardScreensFilters.order,
                        currency: selectedCurrencyName,
                        maxCpm: wizardScreensFilters.maxCpm,
                        userMargin,
                        provider: providerParam,
                        q: searchParam,
                        rotation: rotationParam,
                        slotDuration: slotDurationParam,
                        venueTypeId: venueTypeIdParam,
                        screenType: screenTypeParam,
                        publisherId: publisherIdParam,
                        poiFilters:
                            poiFilters.length > 0
                                ? JSON.stringify(poiFilters)
                                : undefined,
                        restriction: basics?.selectedCreativeCategories?.[0],
                        ...geoParams,
                    });

                    if (!cancelled) {
                        const nextResults = results || [];
                        setWizardScreensData({
                            results: nextResults,
                            total: total || nextResults.length,
                            availableVenueTypeIds,
                            availableVenueTypeCounts,
                            availableRotations,
                            availableRotationCounts,
                            availableSlotDurations,
                            availableSlotDurationCounts,
                            availablePublisherIds,
                            availablePublisherCounts,
                        });
                    }
                } catch (e) {
                    if (!cancelled) {
                        setWizardScreensData({
                            results: [],
                            total: 0,
                            availableVenueTypeIds: [],
                            availableVenueTypeCounts: [],
                            availableRotations: [],
                            availableRotationCounts: [],
                            availableSlotDurations: [],
                            availableSlotDurationCounts: [],
                            availablePublisherIds: [],
                            availablePublisherCounts: [],
                        });
                    }
                } finally {
                    if (!cancelled) setLoadingDataTable(false);
                }
            };

            loadScreens();

            return () => {
                cancelled = true;
            };
        }

        if (usingLocalScreens) {
            setWizardScreensData({
                results: filteredScreensLocal,
                total: filteredScreensLocal.length,
                availableVenueTypeIds:
                    collectVenueTypeIdsFromScreens(filteredScreensLocal),
                availableVenueTypeCounts:
                    collectVenueTypeCountsFromScreens(filteredScreensLocal),
                availableRotations:
                    collectRotationValuesFromScreens(filteredScreensLocal),
                availableRotationCounts:
                    collectRotationCountsFromScreens(filteredScreensLocal),
                availableSlotDurations:
                    collectSlotDurationValuesFromScreens(filteredScreensLocal),
                availableSlotDurationCounts:
                    collectSlotDurationCountsFromScreens(filteredScreensLocal),
                availablePublisherIds:
                    collectPublisherIdsFromScreens(filteredScreensLocal),
                availablePublisherCounts:
                    collectPublisherCountsFromScreens(filteredScreensLocal),
            });
            setLoadingDataTable(false);
            return;
        }

        if (!selectedCurrencyName) return;

        let cancelled = false;

        const loadScreens = async () => {
            setLoadingDataTable(true);
            try {
                const searchParam = wizardScreensFilters.q || undefined;

                const providerParam = providerFilters.length
                    ? providerFilters.join(',')
                    : undefined;

                const rotationValuesParam = Array.isArray(
                    wizardScreensFilters.selectedRotations
                )
                    ? wizardScreensFilters.selectedRotations
                          .map((value) => sanitizeRotationFilter(value))
                          .filter(Boolean)
                    : [];
                const slotDurationValuesParam = Array.isArray(
                    wizardScreensFilters.selectedSlotDurations
                )
                    ? wizardScreensFilters.selectedSlotDurations
                          .map((value) => sanitizeSlotDurationFilter(value))
                          .filter(Boolean)
                    : [];
                const venueTypeValuesParam = Array.isArray(
                    wizardScreensFilters.selectedVenueTypes
                )
                    ? wizardScreensFilters.selectedVenueTypes
                          .map((value) => sanitizeVenueFilter(value))
                          .filter(Boolean)
                    : [];
                const venueTypeIdValues = venueTypeValuesParam.filter((value) =>
                    /^\d+$/.test(value)
                );

                const rotationParam = rotationValuesParam.length
                    ? Array.from(new Set(rotationValuesParam)).join(',')
                    : undefined;
                const slotDurationParam = slotDurationValuesParam.length
                    ? Array.from(new Set(slotDurationValuesParam)).join(',')
                    : undefined;
                const venueTypeIdParam = venueTypeIdValues.length
                    ? Array.from(new Set(venueTypeIdValues)).join(',')
                    : undefined;

                const screenTypeValuesParam = Array.isArray(
                    wizardScreensFilters.selectedScreenTypes
                )
                    ? wizardScreensFilters.selectedScreenTypes
                          .map((value) => {
                              if (typeof value === 'string') {
                                  return formatScreenTypeForRequest(value);
                              }
                              if (value && typeof value === 'object') {
                                  return formatScreenTypeForRequest(
                                      value.value ??
                                          value.name ??
                                          value.type ??
                                          value.id
                                  );
                              }
                              return '';
                          })
                          .filter(Boolean)
                    : [];

                const screenTypeParam = screenTypeValuesParam.length
                    ? Array.from(new Set(screenTypeValuesParam)).join(',')
                    : undefined;

                const publisherIdValues =
                    canFilterByPublisher &&
                    Array.isArray(wizardScreensFilters.selectedPublishers)
                        ? wizardScreensFilters.selectedPublishers
                              .map((publisher) => publisher?.id)
                              .filter((id) => id != null)
                        : [];

                const publisherIdParam = publisherIdValues.length
                    ? Array.from(new Set(publisherIdValues)).join(',')
                    : undefined;

                const geoParams = buildGeoFilterParams(location);
                const poiFilters = buildActivePoiFilters(
                    newCampaign?.location?.zones,
                    filterScreenByPoi,
                    selectedPoiZoneKeys
                );
                const {
                    results,
                    total,
                    availableVenueTypeIds,
                    availableRotations,
                    availableSlotDurations,
                    availablePublisherIds,
                    availableVenueTypeCounts,
                    availableRotationCounts,
                    availableSlotDurationCounts,
                    availablePublisherCounts,
                } = await getScreensPage({
                    page: wizardScreensPage,
                    limit: wizardScreensPerPage,
                    orderBy: wizardScreensFilters.orderBy,
                    order: wizardScreensFilters.order,
                    testing: false,
                    currency: selectedCurrencyName,
                    maxCpm: wizardScreensFilters.maxCpm,
                    userMargin,
                    provider: providerParam,
                    q: searchParam,
                    rotation: rotationParam,
                    slotDuration: slotDurationParam,
                    venueTypeId: venueTypeIdParam,
                    screenType: screenTypeParam,
                    publisherId: publisherIdParam,
                    poiFilters:
                        poiFilters.length > 0
                            ? JSON.stringify(poiFilters)
                            : undefined,
                    restriction: basics?.selectedCreativeCategories?.[0],
                    ...geoParams,
                });

                if (!cancelled) {
                    const nextResults = results || [];
                    setWizardScreensData({
                        results: nextResults,
                        total: total || nextResults.length,
                        availableVenueTypeIds,
                        availableVenueTypeCounts,
                        availableRotations,
                        availableRotationCounts,
                        availableSlotDurations,
                        availableSlotDurationCounts,
                        availablePublisherIds,
                        availablePublisherCounts,
                    });
                }
            } catch (error) {
                if (!cancelled) {
                    setWizardScreensData({
                        results: [],
                        total: 0,
                        availableVenueTypeIds: [],
                        availableVenueTypeCounts: [],
                        availableRotations: [],
                        availableRotationCounts: [],
                        availableSlotDurations: [],
                        availableSlotDurationCounts: [],
                        availablePublisherIds: [],
                        availablePublisherCounts: [],
                    });
                }
                // eslint-disable-next-line no-console
                console.error('getScreensPage failed', error);
            } finally {
                if (!cancelled) {
                    setLoadingDataTable(false);
                }
            }
        };

        loadScreens();

        return () => {
            cancelled = true;
        };
    }, [
        stepper.wizard,
        selectedCurrencyName,
        wizardScreensPage,
        wizardScreensPerPage,
        wizardScreensFilters.providerSource,
        wizardScreensFilters.q,
        wizardScreensFilters.orderBy,
        wizardScreensFilters.order,
        wizardScreensFilters.maxCpm,
        providerFilters,
        usingLocalScreens,
        filteredScreensLocal,
        wizardScreensFilters.selectedRotations,
        wizardScreensFilters.selectedSlotDurations,
        wizardScreensFilters.selectedVenueTypes,
        wizardScreensFilters.selectedScreenTypes,
        wizardScreensFilters.selectedPublishers,
        canFilterByPublisher,
        location,
        filterScreenByPoi,
        selectedPoiZoneKeys,
        newCampaign?.location?.zones,
        basics?.selectedCreativeCategories,
    ]);

    useEffect(() => {
        const totalPages = Math.max(
            1,
            Math.ceil((totalRowsForPagination || 0) / wizardScreensPerPage) || 1
        );
        if (wizardScreensPage > totalPages) {
            setWizardScreensPage(totalPages);
        }
    }, [totalRowsForPagination, wizardScreensPerPage, wizardScreensPage]);

    useEffect(() => {
        dispatch(newCampaignActions.setStepper({ index: 0, wizard: 'basics' }));
        dispatch(newCampaignActions.setCurrentWizard('basics'));
    }, []);

    useEffect(() => {
        if (auth.session?.user?.cpm_mode === 1) {
            const newBudget = Object.assign({}, budget);
            let maxCpm = getCpmSumFromSelectedScreens(
                selectedScreensLocal,
                userMargin,
                selectedCurrencyName
            );
            newBudget.cpm = maxCpm;
            dispatch(newCampaignActions.setBudget(newBudget));
        }
    }, [selectedScreensLocal, auth, selectedCurrencyName]);

    const defaultProps = {
        center: {
            lat: 0,
            lng: 0,
        },
        zoom: 1,
    };

    const applyInitialForecast = (campaignData) => {
        if (campaignData?.forecast) {
            setForecast(campaignData.forecast);
        } else {
            resetForecast();
        }
        setForecastTable(campaignData?.forecastTable || []);
    };

    const allWizards = [
        'basics',
        'location',
        'screens',
        'audiences',
        ...(canManageExpectedStats ? ['expectedStats'] : []),
        'creatives',
        'review',
        'launch',
    ];

    const basicsRef = useRef(null);
    const locationRef = useRef(null);
    const screenRef = useRef(null);
    const audienceRef = useRef(null);
    const creativesRef = useRef(null);
    const reviewRef = useRef(null);

    //TODO agregar las validaciones de cada tab siguiendo locationRef como ejemplo,
    // agregar las funciones de validacion en cada componente wizard y crear un estado local para cada tab para desarmar redux
    const validateWizards = {
        basics: () => {
            validator.validateAll();
            return validator.isValid();
        },
        location: () =>
            locationRef.current?.validate
                ? locationRef.current.validate()
                : true,
        screens: () =>
            screenRef.current?.validate ? screenRef.current.validate() : true,
        audiences: () => {
            validator.validateAll();
            return validator.isValid();
        },
        creatives: () => creativesRef.current?.validate?.() ?? true,
        review: () => true,
    };

    const validateCurrentWizard = (wizard) => {
        const validate = validateWizards[wizard];
        return validate ? validate() : true;
    };

    const changeCurrentWizard = (option) => {
        let valueInsideRange = option;

        if (typeof option === 'number') {
            // ? "review" siempre es el último paso alcanzable con Next/flechas;
            // "launch" solo se alcanza con la acción explícita de lanzar.
            const reviewIndex = wizards.indexOf('review');
            valueInsideRange = Math.max(0, Math.min(option, reviewIndex));
        }

        const goingForward =
            typeof option === 'number'
                ? valueInsideRange > stepper.index
                : true;

        //? valida la tab actual solo si va hacia adelante
        if (goingForward) {
            if (!validateCurrentWizard(stepper.wizard)) {
                return;
            }
        }

        let index =
            typeof option === 'number'
                ? valueInsideRange
                : wizards.indexOf(option);

        let wizard =
            typeof option === 'string' ? option : wizards[valueInsideRange];

        if (wizard === 'launch' && isLaunchBlockedByActiveCampaign) {
            return;
        }

        dispatch(newCampaignActions.setStepper({ index, wizard }));
        dispatch(newCampaignActions.setCurrentWizard(wizard));
    };

    const changeCurrentWizardRef = useRef(changeCurrentWizard);

    const handleExpectedStatsChange = (nextExpectedStats) => {
        dispatch(newCampaignActions.setExpectedStats(nextExpectedStats));
    };

    useEffect(() => {
        changeCurrentWizardRef.current = changeCurrentWizard;
    }, [changeCurrentWizard]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (showModalWarning) return;

            if (e.altKey && !e.shiftKey && !e.metaKey) {
                const key = e.key.toLowerCase();

                if (key === 'a') {
                    e.preventDefault();
                    e.stopPropagation();
                    changeCurrentWizardRef.current(stepper.index - 1);
                }

                if (key === 'd') {
                    e.preventDefault();
                    e.stopPropagation();
                    changeCurrentWizardRef.current(stepper.index + 1);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [stepper.index, showModalWarning]);

    const editCampaign = async (campaignPreview = null) => {
        const campaign =
            campaignPreview ??
            (await service.getCampaignPreview(id, { syncDashboard: false }));
        if (!campaign) return;
        const shouldWarnOnSave =
            normalizeCampaignStatusKey(campaign?.status) === 'ACTIVE';

        setShouldDeactivateOnSave(shouldWarnOnSave);
        confirmedDeactivateOnSaveRef.current = false;
        setCampaignNameWarning(shouldWarnOnSave ? campaign?.name || '' : '');

        const formatedCampaign = await service.editCampaign(campaign);
        if (!formatedCampaign) return;

        applyInitialForecast(formatedCampaign);
        setLocation(formatedCampaign.location);
        const basicsData = _.pick(formatedCampaign, [
            'name',
            'brand',
            'selectedCurrency',
            'budget',
            'timing',
            'dailyCaps',
            'selectedCreativeCategories',
        ]);
        if (!basicsData.dailyCaps) {
            basicsData.dailyCaps = { isActive: false, caps: {} };
        }
        setBasics(basicsData);
        setScreensState({
            selectedScreens: formatedCampaign.selectedScreens,
        });
        setSavedSelectedScreenIds(
            normalizeIds(formatedCampaign.selectedScreens)
        );
        setIsLoadedData(true);
    };

    const onCancelWarning = () => setShowModalWarning(false);
    const onAcceptWarning = async () => {
        setShowModalWarning(false);
        confirmedDeactivateOnSaveRef.current = true;
        const saved = await onSave();

        if (!saved) {
            confirmedDeactivateOnSaveRef.current = false;
            return;
        }
    };

    // if can not launch the campaign, remove tab launch
    const wizards = canLaunchCampaign
        ? allWizards
        : allWizards.filter((w) => w !== 'launch');

    useEffect(async () => {
        const fetchData = async () => {
            if (!isLoadedData) {
                if (id) {
                    if (currencies?.length > 0 && !fetchingData) {
                        setFetchingData(true);
                        setIsSaveEnabled(false);
                        try {
                            const campaign = await service.getCampaignPreview(
                                id,
                                {
                                    syncDashboard: false,
                                }
                            );
                            if (!campaign) return;

                            await editCampaign(campaign);
                        } finally {
                            setFetchingData(false);
                        }
                    }
                } else {
                    setIsLoadedData(true);
                }
            }
        };

        await fetchData();
    }, [currencies.length]);

    useEffect(() => {
        try {
            if (timer === false && usingLocalScreens) {
                const filterScreens = filteredScreensLocal.filter(
                    (filterScreen) => {
                        return screensState.selectedScreens.some(
                            (screen) => screen.id === filterScreen.id
                        );
                    }
                );

                dispatch(screensActions.setSelectedScreensLocal(filterScreens));
                setScreensState({ selectedScreens: filterScreens });
            }
        } catch (error) {
            //
        }
    }, [
        filteredScreensLocal,
        screensState.selectedScreens,
        dispatch,
        timer,
        usingLocalScreens,
    ]);

    if (!isLoadedData) {
        return <Loading isLoading={true} />;
    }

    return (
        <>
            <Helmet>
                <title>{`${t('common.menu.campaigns')} | ${
                    id
                        ? t('common.words.edit') + ' | ' + id
                        : t('common.words.new')
                }`}</title>
            </Helmet>
            <div className="fixed w-full h-screen flex bg-gray-100 overflow-hidden pb-10">
                <Header
                    currentSection={t('common.menu.dashboard')}
                    sectionPath={'/dashboard'}
                    subSection={t('common.words.campaign')}
                    subSectionOne={
                        id ? t('common.words.edit') : t('common.words.new')
                    }
                />
                <div
                    className={`${
                        isResizeMapOn
                            ? 'md:w-3/12'
                            : isResizeMapFullOn
                              ? 'w-full'
                              : 'md:w-9/12'
                    } relative pl-16 pb-10 md:pt-0 mt-12 h-screen flex content-start bg-white border-r border-gray-300 shadow-lg transition-all duration-${resizeMapTransitionDuration}`}
                >
                    <WizardTab
                        calculatingForecast={calculatingForecast}
                        step={stepper?.wizard}
                        stepIndex={stepper.index}
                        canManageExpectedStats={canManageExpectedStats}
                    />
                    {campaignStatusKey && (
                        <div
                            className={`absolute top-2 right-4 mr-2 z-30 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold shadow-sm ${campaignStatusBadgeClasses.badge}`}
                            data-testid="campaign-status-badge"
                        >
                            <span
                                className={`h-2 w-2 rounded-full ${campaignStatusBadgeClasses.dot}`}
                            />
                            {t(`common.campaign.statuses.${campaignStatusKey}`)}
                        </div>
                    )}
                    <div className="w-full h-screen mt-8 px-2 pt-0 pb-24">
                        {isLoadedData ? (
                            <>
                                <WizardBasics
                                    isActive={stepper.wizard === 'basics'}
                                    isResizeMapOn={isResizeMapOn}
                                    currencies={currencies}
                                    currencyDefault={currencyDefault}
                                    selectedCurrency={selectedCurrency}
                                    budgets={budgets}
                                    campaign={newCampaign}
                                    service={service}
                                    totalSpent={totalSpent}
                                    dispatch={dispatch}
                                    isTest={newCampaign.isTest}
                                    validator={validator}
                                    basics={basics}
                                    updateBasics={updateBasics}
                                    selectedScreens={selectedScreensLocal}
                                />
                                <WizardLocation
                                    ref={locationRef}
                                    isActive={stepper.wizard === 'location'}
                                    zones={zones}
                                    isResizeMapOn={isResizeMapOn}
                                    service={service}
                                    location={location}
                                    selectedCurrency={basics.selectedCurrency}
                                    filteredScreensLocal={filteredScreensLocal}
                                    setFilteredScreensLocal={
                                        setFilteredScreensLocal
                                    }
                                    newCampaign={newCampaign}
                                    dispatch={dispatch}
                                    screensWithoutFilter={mapedScreens}
                                    setSelectedScreensLocal={
                                        setSelectedScreensLocal
                                    }
                                    selectedScreensLocal={selectedScreensLocal}
                                    setFilteredScreensByLocation={
                                        setFilteredScreensByLocation
                                    }
                                    fetchingData={fetchingData}
                                    screensWithCpm={screensWithCpm}
                                    auth={auth}
                                    setLoadingDataTable={setLoadingDataTable}
                                    setTimer={setTimer}
                                    setFilterScreenByPoi={setFilterScreenByPoi}
                                    filterScreenByPoi={filterScreenByPoi}
                                    setLocation={setLocation}
                                />
                                {stepper.wizard === 'screens' && (
                                    <WizardScreens
                                        newCampaign={newCampaign}
                                        selectedCurrencyCode={
                                            selectedCurrencyName
                                        }
                                        isResizeMapOn={isResizeMapOn}
                                        rows={paginatedScreens}
                                        totalRows={totalRowsForPagination}
                                        page={wizardScreensPage}
                                        perPage={wizardScreensPerPage}
                                        selectedScreens={selectedScreensLocal}
                                        onChangeSelectedScreens={
                                            handleWizardScreensSelectionChange
                                        }
                                        filters={wizardScreensFilters}
                                        onFiltersChange={
                                            handleWizardScreensFiltersChange
                                        }
                                        onChangePage={
                                            handleWizardScreensPageChange
                                        }
                                        onChangePerPage={
                                            handleWizardScreensPerPageChange
                                        }
                                        loadingDataTable={loadingDataTable}
                                        userMargin={userMargin}
                                        isFetchingData={fetchingData}
                                        screenOpened={screenOpened}
                                        setScreenOpened={setScreenOpened}
                                        filteredScreensLocal={screensForFilters}
                                        screensMatchingFilters={
                                            filteredScreensForTable
                                        }
                                        screenPassesFilters={
                                            screenPassesFilters
                                        }
                                        screenPassesSelectionFilters={
                                            screenPassesSelectionFilters
                                        }
                                        availableVenueTypeIds={
                                            wizardAvailableVenueTypeIds
                                        }
                                        availableVenueTypeCounts={
                                            wizardAvailableVenueTypeCounts
                                        }
                                        availableRotations={
                                            wizardAvailableRotations
                                        }
                                        availableRotationCounts={
                                            wizardAvailableRotationCounts
                                        }
                                        availableSlotDurations={
                                            wizardAvailableSlotDurations
                                        }
                                        availableSlotDurationCounts={
                                            wizardAvailableSlotDurationCounts
                                        }
                                        availablePublisherIds={
                                            wizardAvailablePublisherIds
                                        }
                                        availablePublisherCounts={
                                            wizardAvailablePublisherCounts
                                        }
                                        availableScreensForImport={
                                            screensForFilters
                                        }
                                        ref={screenRef}
                                        tiers={tiers}
                                        screensState={screensState}
                                        setScreensState={setScreensState}
                                        filterScreenByPoi={filterScreenByPoi}
                                        setFilterScreenByPoi={
                                            setFilterScreenByPoi
                                        }
                                        selectedPoiZoneKeys={
                                            selectedPoiZoneKeys
                                        }
                                        onPoiZoneKeysChange={
                                            handlePoiZoneKeysChange
                                        }
                                    />
                                )}
                                <WizardAudiences
                                    isActive={stepper.wizard === 'audiences'}
                                    useSelect={stepper.wizard === 'screens'}
                                    dispatch={dispatch}
                                    service={service}
                                    newCampaign={newCampaign}
                                    campaign={newCampaign}
                                    budgets={budgets}
                                    setSelectedScreensLocal={
                                        setSelectedScreensLocal
                                    }
                                    selectedScreens={selectedScreensLocal}
                                    loadingDataTable={loadingDataTable}
                                    setLoadingDataTable={setLoadingDataTable}
                                    setCalculatingForecast={
                                        setCalculatingForecast
                                    }
                                    calculatingForecast={calculatingForecast}
                                    currencies={currencies}
                                    currencyDefault={currencyDefault}
                                    totalSpent={totalSpent}
                                    selectedScreensLocal={selectedScreensLocal}
                                    tierList={tierList}
                                    validator={validator}
                                    basics={basics}
                                    updateBasics={updateBasics}
                                />

                                {canManageExpectedStats && (
                                    <WizardExpectedStats
                                        isActive={
                                            stepper.wizard === 'expectedStats'
                                        }
                                        selectedScreens={selectedScreensLocal}
                                        expectedStats={expectedStats}
                                        onChangeExpectedStats={
                                            handleExpectedStatsChange
                                        }
                                        selectedCurrencyCode={
                                            selectedCurrencyName
                                        }
                                        userMargin={userMargin}
                                    />
                                )}

                                {hasMountedCreatives && (
                                    <div
                                        className={
                                            stepper.wizard === 'creatives'
                                                ? 'h-full min-h-0'
                                                : 'hidden'
                                        }
                                    >
                                        <CreativesContainer
                                            selectedScreens={
                                                screensState.selectedScreens
                                            }
                                            campaignPreviewId={
                                                effectiveCampaignPreviewId
                                            }
                                            ref={creativesRef}
                                            isDirtyState={isCreativesDirty}
                                            hasUnsavedSelectedScreens={
                                                hasUnsavedSelectedScreens
                                            }
                                        />
                                    </div>
                                )}

                                {stepper.wizard === 'review' && (
                                    <WizardReview
                                        userMargin={userMargin}
                                        isActive={stepper.wizard === 'review'}
                                        newCampaign={newCampaign}
                                        budgets={budgets}
                                        dispatch={dispatch}
                                        setCalculatingForecast={
                                            setCalculatingForecast
                                        }
                                        basics={basics}
                                        location={location}
                                        screensState={screensState}
                                        isLaunchBlockedByActiveCampaign={
                                            isLaunchBlockedByActiveCampaign
                                        }
                                    />
                                )}
                                {canLaunchCampaign && (
                                    <WizardLaunch
                                        isActive={stepper.wizard === 'launch'}
                                        service={service}
                                        newCampaign={newCampaign}
                                    />
                                )}
                            </>
                        ) : (
                            <Loading isLoading={true} />
                        )}
                    </div>
                    <div
                        className={`fixed z-50 flex justify-end -right-1.5 bottom-1 mb-2 mr-4 w-auto gap-2 align-middle`}
                    >
                        <div className="flex gap-2 items-center">
                            <div
                                onClick={() =>
                                    changeCurrentWizard(stepper.index - 1)
                                }
                                className={`${
                                    stepper.wizard === 'basics'
                                        ? 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed hidden'
                                        : 'bg-gray-100 border-gray-300 border text-gray-800 hover:bg-gray-200 cursor-pointer'
                                }
                                flex justify-center items-center w-10 h-10 font-bold rounded-full text-xs shadow-md`}
                            >
                                <Icon name="chevron-left" size={14} />
                            </div>
                            <button
                                data-testid="save-campaign-button"
                                disabled={!isSaveEnabled}
                                className={`flex justify-center items-center w-16 h-16 cursor-pointer font-bold rounded-full text-xs shadow-lg ${
                                    isSaveEnabled
                                        ? 'bg-green-500 hover:bg-green-600 '
                                        : 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed'
                                } transition-colors duration-300`}
                                onClick={() => onSave()}
                            >
                                {!isSaving && (
                                    <Icon
                                        name="save"
                                        size={16}
                                        className="text-white"
                                    />
                                )}
                            </button>
                            <div
                                onClick={() =>
                                    changeCurrentWizard(stepper.index + 1)
                                }
                                className={`${
                                    stepper.wizard === 'launch' &&
                                    canLaunchCampaign
                                        ? 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed'
                                        : stepper.wizard === 'review' &&
                                            (!canLaunchCampaign ||
                                                isLaunchBlockedByActiveCampaign)
                                          ? 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed'
                                          : 'bg-gray-100 border-gray-300 border text-gray-800 hover:bg-gray-200 cursor-pointer'
                                }
                                flex justify-center items-center w-10 h-10 font-bold rounded-full text-xs shadow-md`}
                            >
                                <Icon name="chevron-right" size={14} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="relative pt-10 md:pt-12 hidden md:flex flex-grow h-screen transition-all duration-300">
                    <div className="w-full p-0" id="google-map-canvas">
                        <MapCanvas
                            setScreenOpened={setScreenOpened}
                            screenOpened={screenOpened}
                            lat={defaultProps.center.lat}
                            lng={defaultProps.center.lng}
                            defaultZoom={defaultProps.zoom}
                            resetMap={resetMap}
                            setResetMap={setResetMap}
                            selectedScreensLocal={selectedScreensLocal}
                            screensWithCpm={screensWithCpm}
                            setSelectedScreensLocal={setSelectedScreensLocal}
                            newCampaign={newCampaign}
                            dispatch={dispatch}
                            setFilteredScreensLocal={setFilteredScreensLocal}
                            location={
                                mapLocations?.zones?.length != 0
                                    ? mapLocations
                                    : location
                            }
                            filteredScreensByLocation={
                                stepper.wizard === 'screens'
                                    ? EMPTY_SCREENS
                                    : filteredScreensByLocation
                            }
                            filterScreenByPoi={filterScreenByPoi}
                            currentWizard={stepper.wizard}
                        />
                    </div>
                    <div
                        onClick={() =>
                            dispatch(
                                newCampaignActions.setIsResizeMapOn(
                                    !isResizeMapOn
                                )
                            )
                        }
                        className="absolute z-30 hidden md:flex content-center top-13 left-0 mt-10 -ml-1.5 w-3 h-6 pt-1 rounded-full border border-gray-300 bg-gray-300 opacity-90 hover:opacity-100 hover:bg-white text-white hover:text-gray-400 cursor-pointer hover:shadow"
                    >
                        <Icon
                            name="chevron-down"
                            className={`${
                                isResizeMapOn
                                    ? '-rotate-90 pr-0.5'
                                    : 'rotate-90 pl-0.5'
                            } text-xs transform`}
                        />
                    </div>
                    <div
                        onClick={() =>
                            dispatch(
                                newCampaignActions.setIsResizeMapFullOn(
                                    !isResizeMapFullOn
                                )
                            )
                        }
                        className={`absolute z-30 hidden md:flex content-center top-14 -left-3 rounded-full p-1 border border-gray-300 bg-gray-300 opacity-90 hover:opacity-100 hover:bg-white text-white hover:text-gray-400 cursor-pointer hover:shadow
                             ${isResizeMapOn ? 'bg-gray-300' : 'bg-gray-400'}`}
                    >
                        <Icon
                            name={
                                isResizeMapFullOn
                                    ? 'arrow-from-left'
                                    : 'arrow-from-right'
                            }
                        />
                    </div>
                </div>
            </div>

            <Modal
                size="sm"
                active={showModalWarning}
                toggler={onCancelWarning}
            >
                <ModalHeader toggler={onCancelWarning}>
                    {t('sections.campaigns.new-campaign.modal-warning.title') +
                        campaignNameWarning}
                </ModalHeader>
                <ModalBody>
                    <p className="text-base leading-relaxed text-gray-600 font-normal">
                        {t(
                            'sections.campaigns.new-campaign.modal-warning.description'
                        )}
                    </p>
                </ModalBody>
                <ModalFooter>
                    <CommonButton
                        onClick={onCancelWarning}
                        label={t('common.buttons.cancel')}
                        styleType={'secondary'}
                        className="mx-2"
                    />
                    <CommonButton
                        onClick={onAcceptWarning}
                        label={t(
                            'sections.campaigns.new-campaign.modal-warning.save-continue'
                        )}
                        styleType={'primary'}
                    />
                </ModalFooter>
            </Modal>
        </>
    );
}

export default CreateCampaignPage;
